import { useState, useEffect, useCallback, useRef } from 'react';
import { useAuth } from '../context/AuthContext';
import { DndContext, closestCenter, KeyboardSensor, MouseSensor, TouchSensor, useSensor, useSensors } from '@dnd-kit/core';
import { arrayMove, SortableContext, sortableKeyboardCoordinates, verticalListSortingStrategy, useSortable } from '@dnd-kit/sortable';
import { CSS } from '@dnd-kit/utilities';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Card } from '../components/ui/card';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '../components/ui/tabs';
import { Dialog, DialogContent, DialogDescription, DialogHeader, DialogTitle, DialogTrigger } from '../components/ui/dialog';
import { Label } from '../components/ui/label';
import { Textarea } from '../components/ui/textarea';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../components/ui/select';
import { toast } from 'sonner';
import { Plus, GripVertical, Link2, BarChart3, Settings, Copy, ExternalLink, Trash2, Edit3, Clock, Calendar, Camera, User, Loader2, Eye, Sparkles } from 'lucide-react';
import { linkIcons, socialPlatforms, getLinkIcon, getSocialIcon, IconPicker } from '../components/IconLibrary';
import QRCodeGenerator from '../components/QRCodeGenerator';
import NewBadge from '../components/NewBadge';
import Windows98SkyBackground from '../components/Windows98SkyBackground';
import {
  StarfieldBackground,
  MatrixBackground,
  ParticleBackground,
  AuroraBackground,
  FirefliesBackground,
  BubblesBackground,
  GeometricBackground,
  RainBackground,
  SnowBackground,
  WavesBackground
} from '../components/AnimatedBackgrounds';
import api from '../lib/api';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL || '';

// Helper function to get proper image URL
const getImageUrl = (url) => {
  if (!url) return null;
  // If URL already starts with /api/, use it directly (nginx will proxy)
  if (url.startsWith('/api/')) {
    return BACKEND_URL ? `${BACKEND_URL}${url}` : url;
  }
  if (url.startsWith('http://') || url.startsWith('https://')) {
    return url;
  }
  return BACKEND_URL ? `${BACKEND_URL}${url}` : url;
};

// Theme definitions with animated flag
const THEMES = {
  starfield: {
    name: 'Starfield',
    description: 'Animated flying through space',
    preview: 'linear-gradient(180deg, #0a0a14 0%, #141428 100%)',
    animated: true,
    icon: '✨'
  },
  matrix: {
    name: 'Matrix',
    description: 'Digital rain effect',
    preview: 'linear-gradient(180deg, #000000 0%, #001100 100%)',
    animated: true,
    icon: '🟢'
  },
  particles: {
    name: 'Particles',
    description: 'Connected floating particles',
    preview: 'linear-gradient(180deg, #0a0a14 0%, #0f0a1a 100%)',
    animated: true,
    icon: '⚛️'
  },
  aurora: {
    name: 'Aurora',
    description: 'Northern lights waves',
    preview: 'linear-gradient(180deg, #050510 0%, #0a1020 100%)',
    animated: true,
    icon: '🌌'
  },
  fireflies: {
    name: 'Fireflies',
    description: 'Magical glowing fireflies',
    preview: 'linear-gradient(180deg, #050f05 0%, #0a1a0a 100%)',
    animated: true,
    icon: '🌟'
  },
  bubbles: {
    name: 'Bubbles',
    description: 'Rising underwater bubbles',
    preview: 'linear-gradient(180deg, #0a1428 0%, #0c1929 100%)',
    animated: true,
    icon: '🫧'
  },
  geometric: {
    name: 'Geometric',
    description: 'Rotating shapes',
    preview: 'linear-gradient(180deg, #0a0a14 0%, #141428 100%)',
    animated: true,
    icon: '🔷'
  },
  rain: {
    name: 'Rain',
    description: 'Calming rain effect',
    preview: 'linear-gradient(180deg, #0a0f19 0%, #151f2e 100%)',
    animated: true,
    icon: '🌧️'
  },
  snow: {
    name: 'Snow',
    description: 'Gentle falling snow',
    preview: 'linear-gradient(180deg, #0f1423 0%, #1a2535 100%)',
    animated: true,
    icon: '❄️'
  },
  waves: {
    name: 'Waves',
    description: 'Ocean wave motion',
    preview: 'linear-gradient(180deg, #0a0a14 0%, #0a1428 100%)',
    animated: true,
    icon: '🌊'
  },
  solid: {
    name: 'Solid Dark',
    description: 'Clean dark background',
    preview: '#0f172a',
    animated: false,
    icon: '⬛'
  },
  gradient_cyber: {
    name: 'Cyber',
    description: 'Cyan to purple gradient',
    preview: 'linear-gradient(135deg, #0f172a 0%, #1e1b4b 50%, #0f172a 100%)',
    animated: false,
    icon: '💜'
  },
  gradient_sunset: {
    name: 'Sunset',
    description: 'Warm orange to pink',
    preview: 'linear-gradient(135deg, #1a1a2e 0%, #16213e 50%, #0f0f23 100%)',
    animated: false,
    icon: '🌅'
  },
  gradient_ocean: {
    name: 'Ocean',
    description: 'Deep blue oceanic',
    preview: 'linear-gradient(180deg, #0c1929 0%, #1a365d 50%, #0c1929 100%)',
    animated: false,
    icon: '🌊'
  },
  gradient_forest: {
    name: 'Forest',
    description: 'Dark green nature',
    preview: 'linear-gradient(180deg, #0a1f0a 0%, #1a2f1a 50%, #0a1f0a 100%)',
    animated: false,
    icon: '🌲'
  },
  minimal_dark: {
    name: 'Minimal',
    description: 'Ultra clean dark',
    preview: '#0a0a0a',
    animated: false,
    icon: '🖤'
  },
  windows98: {
    name: 'Windows 98',
    description: 'Rolling hills, drifting clouds, bevel windows',
    preview: 'linear-gradient(180deg, #2f6fb3 0%, #7fb8e8 45%, #d9f0fc 63%, #6fae4a 68%, #2f6f1e 100%)',
    animated: true,
    icon: '🪟'
  }
};

// Animated background component selector
const AnimatedBackgroundPreview = ({ theme, color, color2, containerRef }) => {
  switch (theme) {
    case 'starfield':
      return <StarfieldBackground color={color} containerRef={containerRef} />;
    case 'matrix':
      return <MatrixBackground color={color} containerRef={containerRef} />;
    case 'particles':
      return <ParticleBackground color={color} color2={color2} containerRef={containerRef} />;
    case 'aurora':
      return <AuroraBackground color={color} color2={color2} containerRef={containerRef} />;
    case 'fireflies':
      return <FirefliesBackground color={color} containerRef={containerRef} />;
    case 'bubbles':
      return <BubblesBackground color={color} containerRef={containerRef} />;
    case 'geometric':
      return <GeometricBackground color={color} color2={color2} containerRef={containerRef} />;
    case 'rain':
      return <RainBackground color={color} containerRef={containerRef} />;
    case 'snow':
      return <SnowBackground color={color} containerRef={containerRef} />;
    case 'waves':
      return <WavesBackground color={color} containerRef={containerRef} />;
    default:
      return null;
  }
};

// Helper to check if link is currently scheduled/visible
const isLinkScheduled = (link) => {
  if (!link.scheduled_start && !link.scheduled_end) return null;
  const now = new Date().toISOString();
  if (link.scheduled_start && now < link.scheduled_start) return 'upcoming';
  if (link.scheduled_end && now > link.scheduled_end) return 'expired';
  return 'active';
};

const formatScheduleDate = (isoDate) => {
  if (!isoDate) return null;
  return new Date(isoDate).toLocaleString(undefined, { 
    month: 'short', day: 'numeric', hour: '2-digit', minute: '2-digit' 
  });
};

// Sortable Link Card Component
const SortableLinkCard = ({ link, themeSettings, onEdit, onDelete }) => {
  const {
    attributes,
    listeners,
    setNodeRef,
    transform,
    transition,
    isDragging,
  } = useSortable({ id: link.id });

  const style = {
    transform: CSS.Transform.toString(transform),
    transition,
    opacity: isDragging ? 0.5 : 1,
  };

  const scheduleStatus = isLinkScheduled(link);

  return (
    <Card
      ref={setNodeRef}
      style={style}
      className="win-raised p-4 flex gap-3 items-start group"
      data-testid="creator-link-card"
    >
      <div className="cursor-grab select-none" style={{ touchAction: 'none' }} {...attributes} {...listeners}>
        <GripVertical className="w-5 h-5" />
      </div>
      <div
        className="flex-1 min-w-0 border-l-4 pl-4"
        style={{ borderLeftColor: themeSettings?.box_color || '#d946ef' }}
      >
        <div className="flex items-center gap-3">
          {link.icon && (
            <div className="w-10 h-10 win-well flex items-center justify-center">
              {getLinkIcon(link.icon)}
            </div>
          )}
          <div className="flex-1 min-w-0">
            <h3 className="font-semibold truncate" data-testid="link-title">{link.title}</h3>
            <a
              href={link.url}
              target="_blank"
              rel="noopener noreferrer"
              className="text-sm truncate block"
              data-testid="link-url"
            >
              {link.url}
            </a>
          </div>
        </div>
        <div className="flex items-center gap-4 mt-3 pt-3 border-t border-[var(--win-shadow)] text-sm flex-wrap" data-testid="link-stats-row">
          <span className="flex items-center gap-1">
            <BarChart3 className="w-4 h-4" />
            {link.clicks} clicks
          </span>
          {link.last_clicked && (
            <span>Last: {new Date(link.last_clicked).toLocaleDateString()}</span>
          )}
          {!link.is_active && (
            <span className="text-xs flex items-center gap-1">⚠ Inactive</span>
          )}
          {scheduleStatus === 'upcoming' && (
            <span className="text-xs flex items-center gap-1">
              <Clock className="w-3 h-3" /> Starts {formatScheduleDate(link.scheduled_start)}
            </span>
          )}
          {scheduleStatus === 'expired' && (
            <span className="text-xs flex items-center gap-1">
              <Clock className="w-3 h-3" /> Ended {formatScheduleDate(link.scheduled_end)}
            </span>
          )}
          {scheduleStatus === 'active' && link.scheduled_end && (
            <span className="text-xs flex items-center gap-1">
              <Clock className="w-3 h-3" /> Ends {formatScheduleDate(link.scheduled_end)}
            </span>
          )}
          <div className="sm:hidden ml-auto flex gap-1">
            <Button
              variant="ghost"
              size="sm"
              onClick={() => onEdit(link)}
              className="h-7 w-7 p-0"
              data-testid="edit-link-btn-mobile"
            >
              <Edit3 className="w-4 h-4" />
            </Button>
            <Button
              variant="ghost"
              size="sm"
              onClick={() => onDelete(link.id)}
              className="h-7 w-7 p-0"
              data-testid="delete-link-btn-mobile"
            >
              <Trash2 className="w-4 h-4" />
            </Button>
          </div>
        </div>
      </div>
      <div className="hidden sm:flex gap-2 opacity-0 group-hover:opacity-100 transition-opacity">
        <Button
          variant="ghost"
          size="sm"
          onClick={() => onEdit(link)}
          data-testid="edit-link-btn"
        >
          <Edit3 className="w-4 h-4" />
        </Button>
        <Button
          variant="ghost"
          size="sm"
          onClick={() => onDelete(link.id)}
          data-testid="delete-link-btn"
        >
          <Trash2 className="w-4 h-4" />
        </Button>
      </div>
    </Card>
  );
};

// Live Preview Component
const LivePreview = ({ profile, links, user }) => {
  const themeSettings = profile.theme_settings || {};
  const themeName = themeSettings.theme || 'starfield';
  const theme = THEMES[themeName] || THEMES.starfield;
  const isWindows98 = themeName === 'windows98';
  const previewContainerRef = useRef(null);
  const [previewImageError, setPreviewImageError] = useState(false);
  const displayName = (profile.page_title && profile.page_title.trim()) || user?.display_name || `@${user?.creator_username}`;

  // Reset error state when profile image changes
  useEffect(() => {
    setPreviewImageError(false);
  }, [profile.profile_image]);

  const profileSection = (
    <div className={isWindows98 ? 'text-center' : 'text-center mb-6'}>
      {profile.profile_image && !previewImageError ? (
        <img
          src={getImageUrl(profile.profile_image)}
          alt="Profile"
          className={`w-20 h-20 rounded-full mx-auto mb-3 object-cover ${isWindows98 ? '' : 'border-4'}`}
          style={isWindows98 ? { border: '3px solid var(--win-dark-shadow)' } : { borderColor: themeSettings.link_color || '#06b6d4' }}
          onError={() => setPreviewImageError(true)}
        />
      ) : (
        <div
          className="w-20 h-20 rounded-full mx-auto mb-3 flex items-center justify-center"
          style={isWindows98
            ? { background: 'var(--win-titlebar-start)', border: '3px solid var(--win-dark-shadow)' }
            : { background: `linear-gradient(135deg, ${themeSettings.link_color || '#06b6d4'}, ${themeSettings.box_color || '#d946ef'})` }}
        >
          <User className="w-10 h-10 text-white" />
        </div>
      )}

      {!isWindows98 && (
        <h2 className="text-xl font-bold" style={{ color: themeSettings.name_color || '#06b6d4' }}>
          {displayName}
        </h2>
      )}

      {profile.bio && (
        <p className={isWindows98 ? 'text-sm mt-2 max-w-xs mx-auto' : 'text-slate-300 text-sm mt-2 max-w-xs mx-auto'} style={isWindows98 ? { color: 'var(--win-text)' } : undefined}>
          {profile.bio}
        </p>
      )}

      {/* Social Icons */}
      {profile.social_links && Object.keys(profile.social_links).length > 0 && (
        <div className={isWindows98 ? 'flex flex-wrap justify-center items-center gap-2 mt-4 w-full px-3' : 'flex justify-center gap-2 mt-4'}>
          {Object.entries(profile.social_links).map(([platform]) => (
            <div
              key={platform}
              className={isWindows98 ? 'win-btn win-btn-icon w-9 h-9 flex items-center justify-center' : 'w-8 h-8 rounded-full flex items-center justify-center bg-white/10 text-white'}
            >
              {getSocialIcon(platform, 'w-4 h-4')}
            </div>
          ))}
        </div>
      )}
    </div>
  );

  const linksSection = (
    <div className={isWindows98 ? 'space-y-2 mt-4' : 'space-y-3'}>
      {links.slice(0, 5).map(link => (
        <div
          key={link.id}
          className={isWindows98
            ? 'win-btn text-center font-medium text-sm flex items-center justify-center gap-2 overflow-hidden'
            : 'p-3 rounded-xl text-white text-center font-medium text-sm flex items-center justify-center gap-2 overflow-hidden'}
          style={isWindows98 ? { padding: '10px 12px' } : { background: `linear-gradient(135deg, ${themeSettings.link_color || '#06b6d4'}, ${themeSettings.box_color || '#d946ef'})` }}
        >
          {link.icon && getLinkIcon(link.icon, 'w-4 h-4')}
          <span className="truncate min-w-0">{link.title}</span>
        </div>
      ))}
      {links.length > 5 && (
        <p className={isWindows98 ? 'text-center text-xs' : 'text-slate-500 text-center text-xs'} style={isWindows98 ? { color: 'var(--win-text-disabled)' } : undefined}>+{links.length - 5} more links...</p>
      )}
      {links.length === 0 && (
        <p className={isWindows98 ? 'text-center text-sm' : 'text-slate-500 text-center text-sm'} style={isWindows98 ? { color: 'var(--win-text-disabled)' } : undefined}>No links yet</p>
      )}
    </div>
  );

  return (
    <div
      ref={previewContainerRef}
      className={isWindows98 ? 'overflow-hidden h-[500px] relative' : 'rounded-2xl overflow-hidden border border-white/10 h-[500px] relative'}
      style={isWindows98 ? undefined : { background: theme.preview }}
    >
      {/* Background */}
      {isWindows98 ? (
        <Windows98SkyBackground className="absolute inset-0 z-0 w-full h-full" />
      ) : theme.animated && (
        <div className="absolute inset-0 overflow-hidden" style={{ transform: 'scale(1)', transformOrigin: 'center' }}>
          <AnimatedBackgroundPreview
            theme={themeName}
            color={themeSettings.link_color || '#06b6d4'}
            color2={themeSettings.box_color || '#d946ef'}
            containerRef={previewContainerRef}
          />
        </div>
      )}

      {isWindows98 ? (
        <div className="relative z-10 p-4 h-full overflow-y-auto">
          <div className="win-window">
            <div className="win-titlebar">
              <span className="text-xs tracking-wide truncate">{displayName} — Public Profile</span>
            </div>
            <div className="p-4" style={{ background: 'var(--win-face)', fontFamily: 'var(--font-win)' }}>
              {profileSection}
              {linksSection}
            </div>
          </div>
        </div>
      ) : (
        <div className="relative z-10 p-6 h-full overflow-y-auto">
          {profileSection}
          {linksSection}
        </div>
      )}

      {/* Preview Badge */}
      <div className="absolute top-3 right-3 px-2 py-1 bg-black/50 rounded-full text-xs text-slate-400 flex items-center gap-1">
        {theme.animated && <Sparkles className="w-3 h-3 text-cyan-400" />}
        <Eye className="w-3 h-3" /> Preview
      </div>
    </div>
  );
};

const CreateDashboardPage = () => {
  const { user } = useAuth();
  const [links, setLinks] = useState([]);
  const [analytics, setAnalytics] = useState(null);
  const [profile, setProfile] = useState({
    page_title: '',
    bio: '',
    theme_settings: { theme: 'starfield', link_color: '#06b6d4', box_color: '#d946ef', name_color: '#06b6d4' },
    social_links: {},
    profile_image: null
  });
  const [newLink, setNewLink] = useState({ title: '', url: '', icon: 'globe', scheduled_start: '', scheduled_end: '' });
  const [editingLink, setEditingLink] = useState(null);
  const [showLinkDialog, setShowLinkDialog] = useState(false);
  const [showSocialDialog, setShowSocialDialog] = useState(false);
  const [newSocial, setNewSocial] = useState({ platform: '', url: '' });
  const [loading, setLoading] = useState(true);
  const [uploadingImage, setUploadingImage] = useState(false);
  const [profileImageError, setProfileImageError] = useState(false);
  const profileImageInputRef = useRef(null);

  const sensors = useSensors(
    useSensor(MouseSensor, { activationConstraint: { distance: 8 } }),
    useSensor(TouchSensor, { activationConstraint: { delay: 200, tolerance: 6 } }),
    useSensor(KeyboardSensor, {
      coordinateGetter: sortableKeyboardCoordinates,
    })
  );

  const fetchLinks = useCallback(async () => {
    try {
      const response = await api.get('/creator/links');
      setLinks(response.data);
    } catch (error) {
      console.error('Failed to fetch links:', error);
    }
  }, []);

  const fetchAnalytics = useCallback(async () => {
    try {
      const response = await api.get('/creator/analytics');
      setAnalytics(response.data);
    } catch (error) {
      console.error('Failed to fetch analytics:', error);
    }
  }, []);

  const fetchProfile = useCallback(async () => {
    try {
      const response = await api.get('/creator/profile');
      setProfile({
        page_title: response.data.page_title || '',
        bio: response.data.bio || '',
        theme_settings: response.data.theme_settings || profile.theme_settings,
        social_links: response.data.social_links || {},
        profile_image: response.data.profile_image || null
      });
    } catch (error) {
      console.error('Failed to fetch profile:', error);
    }
  }, []);

  useEffect(() => {
    const loadData = async () => {
      setLoading(true);
      await Promise.all([fetchLinks(), fetchAnalytics(), fetchProfile()]);
      setLoading(false);
    };
    loadData();
  }, [fetchLinks, fetchAnalytics, fetchProfile]);

  const handleCreateLink = async () => {
    if (!newLink.title || !newLink.url) {
      toast.error('Please fill in title and URL');
      return;
    }

    try {
      const linkData = {
        title: newLink.title,
        url: newLink.url,
        icon: newLink.icon,
        scheduled_start: newLink.scheduled_start || null,
        scheduled_end: newLink.scheduled_end || null
      };
      await api.post('/creator/links', linkData);
      setNewLink({ title: '', url: '', icon: 'globe', scheduled_start: '', scheduled_end: '' });
      setShowLinkDialog(false);
      fetchLinks();
      fetchAnalytics();
      toast.success('Link created successfully!');
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to create link');
    }
  };

  const handleUpdateLink = async () => {
    if (!editingLink) return;

    try {
      await api.put(`/creator/links/${editingLink.id}`, {
        title: editingLink.title,
        url: editingLink.url,
        icon: editingLink.icon,
        is_active: editingLink.is_active,
        scheduled_start: editingLink.scheduled_start || null,
        scheduled_end: editingLink.scheduled_end || null
      });
      setEditingLink(null);
      fetchLinks();
      toast.success('Link updated successfully!');
    } catch (error) {
      toast.error('Failed to update link');
    }
  };

  const handleDeleteLink = async (linkId) => {
    try {
      await api.delete(`/creator/links/${linkId}`);
      fetchLinks();
      fetchAnalytics();
      toast.success('Link deleted successfully!');
    } catch (error) {
      toast.error('Failed to delete link');
    }
  };

  const handleSaveProfile = async () => {
    try {
      await api.put('/creator/profile', {
        page_title: profile.page_title,
        bio: profile.bio,
        theme_settings: profile.theme_settings,
        social_links: profile.social_links
      });
      toast.success('Profile saved successfully!');
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to save profile');
    }
  };

  const handleAddSocial = () => {
    if (!newSocial.platform || !newSocial.url) {
      toast.error('Please select a platform and enter a URL');
      return;
    }
    
    const updatedSocials = { ...profile.social_links, [newSocial.platform]: newSocial.url };
    setProfile({ ...profile, social_links: updatedSocials });
    setNewSocial({ platform: '', url: '' });
    setShowSocialDialog(false);
    toast.success('Social link added! Remember to save settings.');
  };

  const handleRemoveSocial = (platform) => {
    const updatedSocials = { ...profile.social_links };
    delete updatedSocials[platform];
    setProfile({ ...profile, social_links: updatedSocials });
  };

  const handleProfileImageUpload = async (e) => {
    const file = e.target.files?.[0];
    if (!file) return;

    if (file.size > 5 * 1024 * 1024) {
      toast.error('Image too large. Max 5MB');
      return;
    }

    setUploadingImage(true);
    setProfileImageError(false); // Reset error state on new upload
    try {
      const formData = new FormData();
      formData.append('file', file);
      const response = await api.post('/creator/profile/image', formData, {
        headers: { 'Content-Type': 'multipart/form-data' }
      });
      setProfile(prev => ({ ...prev, profile_image: response.data.profile_image }));
      toast.success('Profile image uploaded!');
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to upload image');
    } finally {
      setUploadingImage(false);
      if (profileImageInputRef.current) {
        profileImageInputRef.current.value = '';
      }
    }
  };

  const handleDeleteProfileImage = async () => {
    setUploadingImage(true);
    try {
      await api.delete('/creator/profile/image');
      setProfile(prev => ({ ...prev, profile_image: null }));
      setProfileImageError(false); // Reset error state on delete
      toast.success('Profile image removed');
    } catch (error) {
      toast.error('Failed to remove image');
    } finally {
      setUploadingImage(false);
    }
  };

  const handleDragEnd = async (event) => {
    const { active, over } = event;

    if (over && active.id !== over.id) {
      const oldIndex = links.findIndex(link => link.id === active.id);
      const newIndex = links.findIndex(link => link.id === over.id);
      
      const newLinks = arrayMove(links, oldIndex, newIndex);
      setLinks(newLinks);

      try {
        const linkOrders = {};
        newLinks.forEach((link, index) => {
          linkOrders[link.id] = index;
        });
        
        await api.put('/creator/links/reorder', linkOrders);
      } catch (error) {
        console.error('Failed to reorder links:', error);
        toast.error('Failed to save link order');
        fetchLinks();
      }
    }
  };

  const publicUrl = user?.creator_username 
    ? `${process.env.REACT_APP_LINKS_DOMAIN || 'https://links.kybercosplay.com'}/@${user.creator_username}`
    : '';

  const copyPublicUrl = () => {
    navigator.clipboard.writeText(publicUrl);
    toast.success('URL copied to clipboard!');
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center min-h-[400px]">
        <div className="w-8 h-8 border-2 border-[var(--win-shadow)] border-t-transparent rounded-full animate-spin" />
      </div>
    );
  }

  return (
    <div className="max-w-7xl mx-auto" data-testid="create-dashboard">
      {/* Header */}
      <div className="mb-8">
        <h1 className="text-3xl font-heading font-bold mb-2">
          Create
        </h1>
        <p>
          Manage your landing page links and customize your public profile
        </p>
      </div>

      <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
        {/* Main Content */}
        <div className="lg:col-span-2">
          <Tabs defaultValue="links" className="space-y-6">
            <TabsList className="w-full flex">
              <TabsTrigger 
                value="links" 
                className="px-2 sm:px-4 py-2 flex-1 text-xs sm:text-sm"
                data-testid="links-tab"
              >
                <Link2 className="w-4 h-4 sm:mr-2" />
                <span className="hidden sm:inline">Links</span>
              </TabsTrigger>
              <TabsTrigger 
                value="analytics" 
                className="px-2 sm:px-4 py-2 flex-1 text-xs sm:text-sm"
                data-testid="analytics-tab"
              >
                <BarChart3 className="w-4 h-4 sm:mr-2" />
                <span className="hidden sm:inline">Analytics</span>
              </TabsTrigger>
              <TabsTrigger 
                value="settings" 
                className="px-2 sm:px-4 py-2 flex-1 text-xs sm:text-sm"
                data-testid="settings-tab"
              >
                <Settings className="w-4 h-4 sm:mr-2" />
                <span className="hidden sm:inline">Settings</span>
              </TabsTrigger>
            </TabsList>

            {/* Links Tab */}
            <TabsContent value="links" className="space-y-6" data-testid="links-content">
              {/* Public URL Section */}
              {publicUrl && (
                <Card className="win-raised p-3 sm:p-4" data-testid="public-url-section">
                  <Label className="font-semibold mb-2 block text-sm">Your Public Profile URL</Label>
                  <div className="space-y-2">
                    <Input
                      value={publicUrl}
                      readOnly
                      className="w-full text-xs sm:text-sm"
                      data-testid="public-url-input"
                    />
                    <div className="grid grid-cols-3 gap-2">
                      <Button onClick={copyPublicUrl} size="sm" data-testid="copy-url-btn">
                        <Copy className="w-4 h-4 mr-1" />
                        <span className="hidden xs:inline">Copy</span>
                      </Button>
                      <Button
                        variant="outline"
                        size="sm"
                        onClick={() => window.open(`/c/${user?.creator_username}`, '_blank')}
                        data-testid="preview-btn"
                      >
                        <ExternalLink className="w-4 h-4 mr-1" />
                        <span className="hidden xs:inline">View</span>
                      </Button>
                      <QRCodeGenerator 
                        url={`${window.location.origin}/c/${user?.creator_username}`}
                        username={user?.creator_username}
                      />
                    </div>
                  </div>
                </Card>
              )}

              {/* Links Header */}
              <div className="flex flex-col sm:flex-row justify-between items-start sm:items-center gap-3">
                <h2 className="text-xl font-heading font-semibold">Your Links</h2>
                <Dialog open={showLinkDialog} onOpenChange={setShowLinkDialog}>
                  <DialogTrigger asChild>
                    <Button className="w-full sm:w-auto" data-testid="create-link-btn">
                      <Plus className="w-4 h-4 mr-2" />
                      Add Link
                    </Button>
                  </DialogTrigger>
                  <DialogContent className="max-h-[90vh] overflow-y-auto" data-testid="create-link-dialog">
                    <DialogHeader>
                      <DialogTitle>Create New Link</DialogTitle>
                      <DialogDescription>Add a new link to your profile</DialogDescription>
                    </DialogHeader>
                    <div className="space-y-4 mt-4">
                      <div>
                        <Label>Title</Label>
                        <Input
                          value={newLink.title}
                          onChange={(e) => setNewLink({ ...newLink, title: e.target.value })}
                          className="mt-1"
                          placeholder="My Website"
                          data-testid="link-title-input"
                        />
                      </div>
                      <div>
                        <Label>URL</Label>
                        <Input
                          value={newLink.url}
                          onChange={(e) => setNewLink({ ...newLink, url: e.target.value })}
                          className="mt-1"
                          placeholder="https://example.com"
                          data-testid="link-url-input"
                        />
                      </div>
                      <div>
                        <Label>Icon</Label>
                        <IconPicker 
                          value={newLink.icon} 
                          onChange={(icon) => setNewLink({ ...newLink, icon })}
                          type="link"
                        />
                      </div>
                      
                      {/* Scheduling Section */}
                      <div className="border-t border-[var(--win-shadow)] pt-4">
                        <Label className="flex items-center gap-2 mb-3">
                          <Calendar className="w-4 h-4" />
                          Schedule Link (Optional)
                        </Label>
                        <div className="grid grid-cols-2 gap-3">
                          <div>
                            <Label className="text-xs">Start Date & Time</Label>
                            <Input
                              type="datetime-local"
                              value={newLink.scheduled_start}
                              onChange={(e) => setNewLink({ ...newLink, scheduled_start: e.target.value ? new Date(e.target.value).toISOString() : '' })}
                              className="mt-1"
                              data-testid="link-start-input"
                            />
                          </div>
                          <div>
                            <Label className="text-xs">End Date & Time</Label>
                            <Input
                              type="datetime-local"
                              value={newLink.scheduled_end}
                              onChange={(e) => setNewLink({ ...newLink, scheduled_end: e.target.value ? new Date(e.target.value).toISOString() : '' })}
                              className="mt-1"
                              data-testid="link-end-input"
                            />
                          </div>
                        </div>
                        <p className="text-xs mt-2">
                          Leave empty to show the link always.
                        </p>
                      </div>
                      
                      <Button 
                        onClick={handleCreateLink} 
                        className="w-full"
                        data-testid="save-link-btn"
                      >
                        Create Link
                      </Button>
                    </div>
                  </DialogContent>
                </Dialog>
              </div>

              {/* Links Grid */}
              <DndContext
                sensors={sensors}
                collisionDetection={closestCenter}
                onDragEnd={handleDragEnd}
              >
                <SortableContext
                  items={links.map(l => l.id)}
                  strategy={verticalListSortingStrategy}
                >
                  <div className="space-y-3">
                    {links.length === 0 ? (
                      <Card className="win-raised p-8 text-center">
                        <Link2 className="w-12 h-12 mx-auto mb-4" />
                        <p>No links yet. Create your first link to get started!</p>
                      </Card>
                    ) : (
                      links.map(link => (
                        <SortableLinkCard
                          key={link.id}
                          link={link}
                          themeSettings={profile.theme_settings}
                          onEdit={setEditingLink}
                          onDelete={handleDeleteLink}
                        />
                      ))
                    )}
                  </div>
                </SortableContext>
              </DndContext>
            </TabsContent>

            {/* Analytics Tab */}
            <TabsContent value="analytics" className="space-y-6" data-testid="analytics-content">
              <h2 className="text-xl font-heading font-semibold">Analytics Dashboard</h2>
              
              {analytics && (
                <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
                  <Card className="win-raised p-6" data-testid="total-links-card">
                    <p className="text-sm uppercase tracking-wide mb-2">Total Links</p>
                    <div className="win-well p-3">
                      <p className="text-4xl font-bold">
                        {analytics.total_links}
                      </p>
                    </div>
                  </Card>
                  <Card className="win-raised p-6" data-testid="total-clicks-card">
                    <p className="text-sm uppercase tracking-wide mb-2">Total Clicks</p>
                    <div className="win-well p-3">
                      <p className="text-4xl font-bold">
                        {analytics.total_clicks}
                      </p>
                    </div>
                  </Card>
                  {analytics.most_clicked_link && (
                    <Card className="win-raised p-6" data-testid="most-clicked-card">
                      <p className="text-sm uppercase tracking-wide mb-2">Most Clicked</p>
                      <p className="text-lg font-semibold truncate">{analytics.most_clicked_link.title}</p>
                      <p className="text-2xl font-bold">{analytics.most_clicked_link.clicks} clicks</p>
                    </Card>
                  )}
                </div>
              )}
            </TabsContent>

            {/* Settings Tab */}
            <TabsContent value="settings" className="space-y-6" data-testid="settings-content">
              <h2 className="text-xl font-heading font-semibold">Profile Settings</h2>
              
              <div className="space-y-6">
                {/* Profile Image Upload */}
                <Card className="win-raised p-4 sm:p-6">
                  <Label className="mb-3 block font-semibold">Profile Image</Label>
                  <div className="flex flex-col sm:flex-row items-center gap-4">
                    <div className="relative flex-shrink-0">
                      {profile.profile_image && !profileImageError ? (
                        <img 
                          src={getImageUrl(profile.profile_image)}
                          alt="Profile"
                          className="w-24 h-24 rounded-full object-cover border-4 border-[var(--win-shadow)]"
                          data-testid="creator-profile-image"
                          onError={() => {
                            console.error('Failed to load profile image:', profile.profile_image);
                            setProfileImageError(true);
                          }}
                        />
                      ) : (
                        <div className="w-24 h-24 rounded-full bg-[var(--win-shadow)] flex items-center justify-center">
                          <User className="w-12 h-12 text-white" />
                        </div>
                      )}
                      {uploadingImage && (
                        <div className="absolute inset-0 flex items-center justify-center bg-black/50 rounded-full">
                          <Loader2 className="w-8 h-8 text-white animate-spin" />
                        </div>
                      )}
                    </div>
                    <div className="flex flex-row sm:flex-col gap-2 w-full sm:w-auto">
                      <input
                        ref={profileImageInputRef}
                        type="file"
                        accept="image/jpeg,image/png,image/webp,image/gif"
                        onChange={handleProfileImageUpload}
                        className="hidden"
                        data-testid="profile-image-input"
                      />
                      <Button
                        type="button"
                        variant="outline"
                        size="sm"
                        onClick={() => profileImageInputRef.current?.click()}
                        disabled={uploadingImage}
                        className="flex-1 sm:flex-none"
                        data-testid="upload-profile-image-btn"
                      >
                        <Camera className="w-4 h-4 mr-2" />
                        Upload
                      </Button>
                      {profile.profile_image && (
                        <Button
                          type="button"
                          variant="ghost"
                          size="sm"
                          onClick={handleDeleteProfileImage}
                          disabled={uploadingImage}
                          className="flex-1 sm:flex-none"
                          data-testid="delete-profile-image-btn"
                        >
                          <Trash2 className="w-4 h-4 mr-2" />
                          Remove
                        </Button>
                      )}
                    </div>
                  </div>
                  <p className="text-xs mt-3 text-center sm:text-left">
                    Max 5MB. Supports JPEG, PNG, WebP, GIF.
                  </p>
                </Card>
                
                {/* Page Title */}
                <Card className="win-raised p-6">
                  <Label className="font-semibold inline-flex items-center gap-2">Page Title <NewBadge since="2026-06-20" /></Label>
                  <Input
                    value={profile.page_title}
                    onChange={(e) => setProfile({ ...profile, page_title: e.target.value })}
                    className="mt-2"
                    placeholder={user?.display_name || `@${user?.creator_username}`}
                    maxLength={60}
                    data-testid="page-title-input"
                  />
                  <p className="text-xs mt-2">
                    The name shown at the top of your links page. Leave blank to use your display name.
                  </p>
                </Card>

                {/* Bio */}
                <Card className="win-raised p-6">
                  <Label className="font-semibold">Bio</Label>
                  <Textarea
                    value={profile.bio}
                    onChange={(e) => setProfile({ ...profile, bio: e.target.value })}
                    className="mt-2"
                    placeholder="Tell visitors about yourself..."
                    rows={4}
                    data-testid="bio-input"
                  />
                </Card>

                {/* Theme Selection */}
                <Card className="win-raised p-6">
                  <Label className="font-semibold mb-2 block">Theme</Label>
                  <p className="text-sm mb-4">Choose a background style for your profile</p>
                  
                  {/* Animated Themes Section */}
                  <div className="mb-4">
                    <p className="text-xs font-semibold mb-2 flex items-center gap-1">
                      <Sparkles className="w-3 h-3" /> ANIMATED THEMES
                    </p>
                    <div className="grid grid-cols-2 md:grid-cols-5 gap-3">
                      {Object.entries(THEMES).filter(([_, t]) => t.animated).map(([key, theme]) => (
                        <button
                          key={key}
                          onClick={() => setProfile({ 
                            ...profile, 
                            theme_settings: { ...profile.theme_settings, theme: key } 
                          })}
                          className={`p-2 ${
                            profile.theme_settings?.theme === key ? 'win-selected' : ''
                          }`}
                          data-testid={`theme-${key}`}
                        >
                          <div 
                            className="w-full h-12 rounded-lg mb-1 flex items-center justify-center text-lg"
                            style={{ background: theme.preview }}
                          >
                            {theme.icon}
                          </div>
                          <p className="text-xs font-medium truncate">{theme.name}</p>
                        </button>
                      ))}
                    </div>
                  </div>
                  
                  {/* Static Themes Section */}
                  <div>
                    <p className="text-xs font-semibold mb-2">STATIC THEMES</p>
                    <div className="grid grid-cols-2 md:grid-cols-6 gap-2">
                      {Object.entries(THEMES).filter(([_, t]) => !t.animated).map(([key, theme]) => (
                        <button
                          key={key}
                          onClick={() => setProfile({ 
                            ...profile, 
                            theme_settings: { ...profile.theme_settings, theme: key } 
                          })}
                          className={`p-2 ${
                            profile.theme_settings?.theme === key ? 'win-selected' : ''
                          }`}
                          data-testid={`theme-${key}`}
                        >
                          <div 
                            className="w-full h-10 rounded-lg mb-1 flex items-center justify-center text-sm"
                            style={{ background: theme.preview }}
                          >
                            {theme.icon}
                          </div>
                          <p className="text-xs font-medium truncate">{theme.name}</p>
                        </button>
                      ))}
                    </div>
                  </div>
                </Card>

                {/* Colors */}
                <Card className="win-raised p-6">
                  <Label className="font-semibold mb-4 block">Colors</Label>
                  <div className="grid grid-cols-1 sm:grid-cols-3 gap-4">
                    <div>
                      <Label className="text-sm">Link Color</Label>
                      <div className="flex items-center gap-2 mt-2">
                        <Input
                          type="color"
                          value={profile.theme_settings?.link_color || '#06b6d4'}
                          onChange={(e) => setProfile({ 
                            ...profile, 
                            theme_settings: { ...profile.theme_settings, link_color: e.target.value } 
                          })}
                          className="w-12 h-10 cursor-pointer p-1 flex-shrink-0"
                          data-testid="link-color-input"
                        />
                        <Input
                          value={profile.theme_settings?.link_color || '#06b6d4'}
                          onChange={(e) => setProfile({ 
                            ...profile, 
                            theme_settings: { ...profile.theme_settings, link_color: e.target.value } 
                          })}
                          className="flex-1 min-w-0"
                        />
                      </div>
                    </div>
                    <div>
                      <Label className="text-sm">Accent Color</Label>
                      <div className="flex items-center gap-2 mt-2">
                        <Input
                          type="color"
                          value={profile.theme_settings?.box_color || '#d946ef'}
                          onChange={(e) => setProfile({ 
                            ...profile, 
                            theme_settings: { ...profile.theme_settings, box_color: e.target.value } 
                          })}
                          className="w-12 h-10 cursor-pointer p-1 flex-shrink-0"
                          data-testid="box-color-input"
                        />
                        <Input
                          value={profile.theme_settings?.box_color || '#d946ef'}
                          onChange={(e) => setProfile({ 
                            ...profile, 
                            theme_settings: { ...profile.theme_settings, box_color: e.target.value } 
                          })}
                          className="flex-1 min-w-0"
                        />
                      </div>
                    </div>
                    <div>
                      <Label className="text-sm">Name Color</Label>
                      <div className="flex items-center gap-2 mt-2">
                        <Input
                          type="color"
                          value={profile.theme_settings?.name_color || '#06b6d4'}
                          onChange={(e) => setProfile({ 
                            ...profile, 
                            theme_settings: { ...profile.theme_settings, name_color: e.target.value } 
                          })}
                          className="w-12 h-10 cursor-pointer p-1 flex-shrink-0"
                          data-testid="name-color-input"
                        />
                        <Input
                          value={profile.theme_settings?.name_color || '#06b6d4'}
                          onChange={(e) => setProfile({ 
                            ...profile, 
                            theme_settings: { ...profile.theme_settings, name_color: e.target.value } 
                          })}
                          className="flex-1 min-w-0"
                        />
                      </div>
                    </div>
                  </div>
                </Card>

                {/* Social Links */}
                <Card className="win-raised p-6">
                  <Label className="font-semibold mb-4 block">Social Links</Label>
                  {profile.social_links && Object.entries(profile.social_links).map(([platform, url]) => (
                    <div key={platform} className="win-well p-3 mb-2">
                      <div className="flex items-center justify-between mb-2">
                        <div className="flex items-center gap-2">
                          {getSocialIcon(platform)}
                          <span className="font-medium">{socialPlatforms[platform]?.label || platform}</span>
                        </div>
                        <Button
                          variant="ghost"
                          size="sm"
                          onClick={() => handleRemoveSocial(platform)}
                        >
                          Remove
                        </Button>
                      </div>
                      <Input value={url} readOnly className="text-sm" />
                    </div>
                  ))}
                  
                  <Dialog open={showSocialDialog} onOpenChange={setShowSocialDialog}>
                    <DialogTrigger asChild>
                      <button
                        className="win-btn w-full flex items-center justify-center gap-2 mt-2"
                        data-testid="add-social-btn"
                      >
                        <Plus className="w-5 h-5" />
                        Add Social Link
                      </button>
                    </DialogTrigger>
                    <DialogContent>
                      <DialogHeader>
                        <DialogTitle>Add Social Link</DialogTitle>
                        <DialogDescription>Connect your social media accounts</DialogDescription>
                      </DialogHeader>
                      <div className="space-y-4 mt-4">
                        <div>
                          <Label>Platform</Label>
                          <Select value={newSocial.platform} onValueChange={(v) => setNewSocial({ ...newSocial, platform: v, url: '' })}>
                            <SelectTrigger className="mt-1" data-testid="social-platform-select">
                              <SelectValue placeholder="Select platform" />
                            </SelectTrigger>
                            <SelectContent>
                              {Object.entries(socialPlatforms).map(([key, data]) => (
                                <SelectItem key={key} value={key}>{data.label}</SelectItem>
                              ))}
                            </SelectContent>
                          </Select>
                        </div>
                        {newSocial.platform && (
                          <div>
                            <Label>URL or Username</Label>
                            <Input
                              value={newSocial.url}
                              onChange={(e) => setNewSocial({ ...newSocial, url: e.target.value })}
                              className="mt-1"
                              placeholder={socialPlatforms[newSocial.platform]?.placeholder}
                              data-testid="social-url-input"
                            />
                          </div>
                        )}
                        <Button onClick={handleAddSocial} className="w-full" data-testid="save-social-btn">
                          Add Social Link
                        </Button>
                      </div>
                    </DialogContent>
                  </Dialog>
                </Card>

                <Button onClick={handleSaveProfile} className="w-full" data-testid="save-settings-btn">
                  Save Settings
                </Button>
              </div>
            </TabsContent>
          </Tabs>
        </div>

        {/* Live Preview Sidebar - Hidden on mobile, shown on lg screens */}
        <div className="hidden lg:block lg:col-span-1">
          <div className="sticky top-6">
            <h3 className="font-semibold mb-4 flex items-center gap-2">
              <Eye className="w-4 h-4" />
              Live Preview
            </h3>
            <LivePreview profile={profile} links={links} user={user} />
          </div>
        </div>
      </div>

      {/* Mobile Live Preview - Show at bottom on mobile only */}
      <div className="lg:hidden mt-6">
        <Card className="win-raised p-4">
          <h3 className="font-semibold mb-4 flex items-center gap-2">
            <Eye className="w-4 h-4" />
            Live Preview
          </h3>
          <LivePreview profile={profile} links={links} user={user} />
        </Card>
      </div>

      {/* Edit Link Dialog */}
      {editingLink && (
        <Dialog open={!!editingLink} onOpenChange={() => setEditingLink(null)}>
          <DialogContent className="max-h-[90vh] overflow-y-auto" data-testid="edit-link-dialog">
            <DialogHeader>
              <DialogTitle>Edit Link</DialogTitle>
              <DialogDescription>Update your link details</DialogDescription>
            </DialogHeader>
            <div className="space-y-4 mt-4">
              <div>
                <Label>Title</Label>
                <Input
                  value={editingLink.title}
                  onChange={(e) => setEditingLink({ ...editingLink, title: e.target.value })}
                  className="mt-1"
                  data-testid="edit-link-title-input"
                />
              </div>
              <div>
                <Label>URL</Label>
                <Input
                  value={editingLink.url}
                  onChange={(e) => setEditingLink({ ...editingLink, url: e.target.value })}
                  className="mt-1"
                  data-testid="edit-link-url-input"
                />
              </div>
              <div>
                <Label>Icon</Label>
                <IconPicker 
                  value={editingLink.icon} 
                  onChange={(icon) => setEditingLink({ ...editingLink, icon })}
                  type="link"
                />
              </div>
              <div>
                <Label>Status</Label>
                <Select
                  value={editingLink.is_active ? 'true' : 'false'}
                  onValueChange={(v) => setEditingLink({ ...editingLink, is_active: v === 'true' })}
                >
                  <SelectTrigger className="mt-1" data-testid="edit-link-active-select">
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="true">Active</SelectItem>
                    <SelectItem value="false">Inactive</SelectItem>
                  </SelectContent>
                </Select>
              </div>
              
              {/* Scheduling Section */}
              <div className="border-t border-[var(--win-shadow)] pt-4">
                <Label className="flex items-center gap-2 mb-3">
                  <Calendar className="w-4 h-4" />
                  Schedule Link
                </Label>
                <div className="grid grid-cols-2 gap-3">
                  <div>
                    <Label className="text-xs">Start Date & Time</Label>
                    <Input
                      type="datetime-local"
                      value={editingLink.scheduled_start ? new Date(editingLink.scheduled_start).toISOString().slice(0, 16) : ''}
                      onChange={(e) => setEditingLink({ ...editingLink, scheduled_start: e.target.value ? new Date(e.target.value).toISOString() : null })}
                      className="mt-1"
                      data-testid="edit-link-start-input"
                    />
                  </div>
                  <div>
                    <Label className="text-xs">End Date & Time</Label>
                    <Input
                      type="datetime-local"
                      value={editingLink.scheduled_end ? new Date(editingLink.scheduled_end).toISOString().slice(0, 16) : ''}
                      onChange={(e) => setEditingLink({ ...editingLink, scheduled_end: e.target.value ? new Date(e.target.value).toISOString() : null })}
                      className="mt-1"
                      data-testid="edit-link-end-input"
                    />
                  </div>
                </div>
                {(editingLink.scheduled_start || editingLink.scheduled_end) && (
                  <Button
                    type="button"
                    variant="ghost"
                    size="sm"
                    onClick={() => setEditingLink({ ...editingLink, scheduled_start: null, scheduled_end: null })}
                    className="mt-2"
                  >
                    Clear Schedule
                  </Button>
                )}
              </div>
              
              <Button onClick={handleUpdateLink} className="w-full" data-testid="update-link-btn">
                Update Link
              </Button>
            </div>
          </DialogContent>
        </Dialog>
      )}
    </div>
  );
};

export default CreateDashboardPage;
